import { Button } from '@1trading/react/components/ui/button';
import { Card, CardContent } from '@1trading/react/components/ui/card';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { BUSINESS_TYPES, BusinessType } from '@/constants/institution';
import { InstitutionFieldCountry } from '@/features/institution/components/fields/country';
import { InstitutionFieldsetAddress } from '@/features/institution/components/fieldsets/address';
import { InstitutionFieldsetDate } from '@/features/institution/components/fieldsets/date';
import { createInstitutionCorporateOwner, type InstitutionOwnership, type InstitutionOwnershipOrDefaults } from '@/features/institution/ownership/schema';
import { useFormFieldArray } from '@/hooks/use-form-field-array';
import type { FormStepGuard } from '@/hooks/use-form-step-guard';

const InstitutionOwnershipFieldsetCorporateOwnersLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.ownership.corporateOwners.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.ownership.corporateOwners.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionOwnershipFieldsetCorporateOwnersLegend.displayName = 'InstitutionOwnershipFieldsetCorporateOwnersLegend';

const InstitutionOwnershipFieldsetCorporateOwner = ({ index, onRemove }: { index: number; onRemove?: () => void }) => {
  const form = useFormContext<InstitutionOwnershipOrDefaults>();
  const { t } = useTranslation();
  const typeOfBusiness = form.watch(`corporateOwners.${index}.typeOfBusiness`);

  return (
    <Card className="shadow-none" bordered>
      <CardContent className="pt-6">
        <fieldset className="relative space-y-6">
          <legend className="font-medium">
            {t('institutionDetails.ownership.corporateOwners.corporateOwner')} {index + 1}
          </legend>
          {onRemove ? (
            <Button className="absolute top-[-0.66rem] right-[-0.66rem] !m-0 text-destructive hover:text-destructive" size="icon" type="button" variant="ghost" onClick={onRemove}>
              <Trash2 className="size-4" />
              <span className="sr-only">
                {t('institutionDetails.ownership.corporateOwners.remove')} {index + 1}
              </span>
            </Button>
          ) : null}
          <FormField
            control={form.control}
            name={`corporateOwners.${index}.companyName`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.corporateOwner.companyName')}*</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('institutionDetails.corporateOwner.placeholders.companyName')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`corporateOwners.${index}.registrationNumber`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.corporateOwner.registrationNumber')}*</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('institutionDetails.corporateOwner.placeholders.registrationNumber')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <InstitutionFieldsetDate<InstitutionOwnershipOrDefaults>
            legend={t('institutionDetails.corporateOwner.dateOfIncorporation')}
            name={`corporateOwners.${index}.dateOfIncorporation`}
          />
          <InstitutionFieldCountry<InstitutionOwnershipOrDefaults>
            label={t('institutionDetails.corporateOwner.countryOfIncorporation')}
            name={`corporateOwners.${index}.countryOfIncorporation`}
            placeholder={t('institutionDetails.corporateOwner.placeholders.countryOfIncorporation')}
          />
          <fieldset className="space-y-6">
            <legend className="font-medium">{t('institutionDetails.corporateOwner.registeredAddress')}</legend>
            <InstitutionFieldsetAddress<InstitutionOwnershipOrDefaults> name={`corporateOwners.${index}.registeredAddress`} />
          </fieldset>
          <FormField
            control={form.control}
            name={`corporateOwners.${index}.typeOfBusiness`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.corporateOwner.typeOfBusiness')}*</FormLabel>
                <Select defaultValue={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('institutionDetails.corporateOwner.placeholders.typeOfBusiness')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {BUSINESS_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(`institutionDetails.company.typeOfBusiness.options.${type}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          {typeOfBusiness === BusinessType.OTHER ? (
            <FormField
              control={form.control}
              name={`corporateOwners.${index}.typeOfBusinessOther`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.corporateOwner.typeOfBusinessOther')}*</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t('institutionDetails.corporateOwner.placeholders.typeOfBusinessOther')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
          <FormField
            control={form.control}
            name={`corporateOwners.${index}.taxId`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.corporateOwner.taxId')}*</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('institutionDetails.corporateOwner.placeholders.taxId')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`corporateOwners.${index}.leiNumber`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.corporateOwner.leiNumber')}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('institutionDetails.corporateOwner.placeholders.leiNumber')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`corporateOwners.${index}.website`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.corporateOwner.website')}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('institutionDetails.corporateOwner.placeholders.website')} inputMode="url" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`corporateOwners.${index}.ownershipPct`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.corporateOwner.ownershipPct')}*</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="no-spinner"
                    inputMode="decimal"
                    placeholder={t('institutionDetails.ownership.placeholders.percentage')}
                    type="number"
                    value={field.value ?? ''}
                    onChange={(event) => field.onChange(event.target.value === '' ? undefined : Number(event.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </fieldset>
      </CardContent>
    </Card>
  );
};
InstitutionOwnershipFieldsetCorporateOwner.displayName = 'InstitutionOwnershipFieldsetCorporateOwner';

const InstitutionOwnershipFieldsetCorporateOwners = () => {
  const form = useFormContext<InstitutionOwnershipOrDefaults>();
  const { fields, append, remove } = useFormFieldArray({ control: form.control, name: 'corporateOwners' });
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name="corporateOwners"
      render={({ fieldState: { error } }) => (
        <FormItem>
          <div className="space-y-6">
            {fields.map((corporateOwner, index) => (
              <InstitutionOwnershipFieldsetCorporateOwner key={corporateOwner.id} index={index} onRemove={fields.length > 1 ? () => remove(index) : undefined} />
            ))}
            <Button className="gap-2" size="sm" type="button" variant="outline" onClick={() => append(createInstitutionCorporateOwner())}>
              <Plus className="size-4" />
              {t('institutionDetails.ownership.corporateOwners.add')}
            </Button>
          </div>
          {error?.root?.message || error?.message ? <FormMessage>{error.root?.message ?? error.message}</FormMessage> : null}
        </FormItem>
      )}
    />
  );
};
InstitutionOwnershipFieldsetCorporateOwners.displayName = 'InstitutionOwnershipFieldsetCorporateOwners';

const beforeStepEnter: FormStepGuard<InstitutionOwnership> = async ({ direction, form, steps }, next) => {
  if (!form) return next(false);

  if (form.getValues('hasCorporateOwners') !== 'YES') return next(steps.indexOf(InstitutionOwnershipFieldsetCorporateOwners) + (direction === 'next' ? 1 : -1));

  return next();
};

InstitutionOwnershipFieldsetCorporateOwners.beforeStepEnter = beforeStepEnter;

export { InstitutionOwnershipFieldsetCorporateOwners, InstitutionOwnershipFieldsetCorporateOwnersLegend };
